import { useTranslation } from 'react-i18next';

import type { Workspace } from '@/entities/workspace';
import { CreateMenu as SharedCreateMenu, type CreateItem } from '@/features/create-menu';
import { Icons } from '@/shared/ui';

/** Team workspaces offered for "New task in ..." before the long tail is left to the workspace. */
const MAX_TEAM_TASK_TARGETS = 5;

interface Props {
  /** The personal workspace (boards and tasks can always be created there). */
  personal: Workspace | undefined;
  /** Team workspaces where the viewer may create tasks. */
  taskTargets: readonly Workspace[];
  onNewTask: (workspace: Workspace) => void;
  onNewBoard: () => void;
  /** Offered when some workspace allows the viewer to create databases. */
  onNewDatabase?: () => void;
  onNewWorkspace: () => void;
}

/** The home "Create" menu: creation only (importing is a separate action beside it). */
export function CreateMenu({
  personal,
  taskTargets,
  onNewTask,
  onNewBoard,
  onNewDatabase,
  onNewWorkspace,
}: Props) {
  const { t } = useTranslation();
  const items: CreateItem[] = [];
  if (personal) {
    items.push({
      id: 'task-personal',
      label: t('home.newTask'),
      icon: <Icons.check />,
      onSelect: () => {
        onNewTask(personal);
      },
    });
  }
  for (const workspace of taskTargets.slice(0, MAX_TEAM_TASK_TARGETS)) {
    items.push({
      id: `task-${workspace.id}`,
      label: t('home.newTaskIn', { name: workspace.name }),
      icon: <Icons.check />,
      onSelect: () => {
        onNewTask(workspace);
      },
    });
  }
  if (personal) {
    items.push({
      id: 'board',
      label: t('workspace.newBoard'),
      icon: <Icons.board />,
      separated: true,
      onSelect: onNewBoard,
    });
  }
  if (onNewDatabase) {
    items.push({
      id: 'database',
      label: t('databases.new'),
      icon: <Icons.layers />,
      onSelect: onNewDatabase,
    });
  }
  items.push({
    id: 'workspace',
    label: t('home.newWorkspace'),
    icon: <Icons.workspace />,
    onSelect: onNewWorkspace,
  });
  return <SharedCreateMenu items={items} />;
}
